import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wallet, Receipt, FileStack, Building2, Download, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import { getPurchaseRegister, type PurchaseLine } from '@/api/accounts';
import { SegGroup } from './SegGroup';
import { RegisterTable } from './RegisterTable';

const money = (v: number) => <span className="tabular-nums">{formatINR(v)}</span>;

type View = 'line' | 'vend' | 'item' | 'hsn';

export function PurchaseRegisterPage() {
  const [view, setView] = useState<View>('line');
  // All / Date (from–to) / Month / Quarter / Year — opens on the current month.
  const period = usePeriod('month');
  const { range } = period;
  const [vendor, setVendor] = useState('');
  const [search, setSearch] = useState('');
  const [exportTick, setExportTick] = useState(0);
  const viewName = { line: 'By_GRN_Line', vend: 'By_Vendor', item: 'By_Item', hsn: 'By_HSN' }[view];
  const exp = { exportSignal: exportTick, exportName: `Purchase_Register_${viewName}` };

  const query = useQuery({
    queryKey: ['accounts', 'purchase-register', range?.from ?? '', range?.to ?? ''],
    queryFn: () => getPurchaseRegister({ from: range?.from, to: range?.to }),
  });
  const allLines = useMemo(() => query.data?.lines ?? [], [query.data]);
  const vendorOpts = useMemo(() => [...new Set(allLines.map((l) => l.vendor))].sort(), [allLines]);
  const lines = useMemo(() => allLines.filter((l) => !vendor || l.vendor === vendor), [allLines, vendor]);

  const kpis = useMemo(() => ({
    grossPurchases: lines.reduce((s, l) => s + l.taxable, 0),
    inputGst: lines.reduce((s, l) => s + l.inputGst, 0),
    bills: new Set(lines.map((l) => l.grn)).size,
    suppliers: new Set(lines.map((l) => l.vendor)).size,
  }), [lines]);

  const byVendor = useMemo(() => groupPurchase(lines, (l) => l.vendor).map(([vendor, g]) => ({ ...g, vendor })), [lines]);
  const byItem = useMemo(() => groupPurchase(lines, (l) => l.item).map(([item, g]) => ({ item, ...g })), [lines]);
  const byHsn = useMemo(() => groupHsn(lines), [lines]);

  return (
    <div>
      <SectionHeader
        title="Purchase Register"      />

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Gross Purchases (taxable)" value={`₹${formatINR(kpis.grossPurchases)}`} accent="#FF8C00" Icon={Wallet} loading={query.isLoading} />
        <KpiCard label="Input GST (ITC)" value={`₹${formatINR(kpis.inputGst)}`} accent="#8b5cf6" Icon={Receipt} loading={query.isLoading} />
        <KpiCard label="Bills / GRNs" value={kpis.bills} accent="#5b8def" Icon={FileStack} loading={query.isLoading} />
        <KpiCard label="Active Suppliers" value={kpis.suppliers} accent="#35c07f" Icon={Building2} loading={query.isLoading} />
      </div>

      <SegGroup
        value={view}
        onChange={(v) => setView(v as View)}
        options={[
          { value: 'line', label: 'By GRN line' },
          { value: 'vend', label: 'By Vendor' },
          { value: 'item', label: 'By Item' },
          { value: 'hsn', label: 'By HSN' },
        ]}
      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              title="Search by GRN, vendor, item or HSN"
              className="!pl-5 sm:!pl-8"
              aria-label="Search purchase register"
            />
          </div>
        </FilterField>
        <FilterField label="Vendor">
          <Select value={vendor} onChange={(e) => setVendor(e.target.value)}>
            <option value="">All</option>
            {vendorOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          title="Export to Excel — every row matching these filters and search"
          onClick={() => setExportTick((t) => t + 1)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:gap-1.5 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      {view === 'line' && (
        <RegisterTable
          {...exp} title="GRN-line detail" count={`${lines.length} lines`} search={search} searchText={(r) => `${r.grn} ${r.vendor} ${r.item} ${r.hsn}`} loading={query.isLoading}
          rows={lines} rowKey={(r) => `${r.grn}|${r.item}|${r.taxable}|${r.qty}`}
          columns={[
            { key: 'grn', header: 'GRN / Bill', cell: (r) => <span className="font-extrabold text-accent">{r.grn}</span> },
            { key: 'date', header: 'Date', cell: (r) => r.date },
            { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-bold">{r.vendor}</span> },
            { key: 'item', header: 'Item', cell: (r) => r.item },
            { key: 'hsn', header: 'HSN', cell: (r) => r.hsn },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'rate', header: 'Rate', align: 'right', cell: (r) => money(r.rate) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'gst', header: 'GST%', align: 'center', cell: (r) => <span className="tabular-nums">{r.gst}</span> },
            { key: 'inputGst', header: 'Input GST', align: 'right', cell: (r) => money(r.inputGst) },
            { key: 'total', header: 'Line Total', align: 'right', cell: (r) => money(r.total) },
          ]}
        />
      )}

      {view === 'vend' && (
        <RegisterTable
          {...exp} title="By vendor" count={`${byVendor.length} vendors`} search={search} searchText={(r) => r.vendor} loading={query.isLoading}
          rows={byVendor} rowKey={(r) => r.vendor}
          columns={[
            { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-bold">{r.vendor}</span> },
            { key: 'bills', header: 'Bills', align: 'right', cell: (r) => <span className="tabular-nums">{r.bills}</span> },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'inputGst', header: 'Input GST', align: 'right', cell: (r) => money(r.inputGst) },
            { key: 'total', header: 'Total', align: 'right', cell: (r) => money(r.total) },
          ]}
        />
      )}

      {view === 'item' && (
        <RegisterTable
          {...exp} title="By item" count={`${byItem.length} items`} search={search} searchText={(r) => `${r.item} ${r.hsn} ${r.vendor}`} loading={query.isLoading}
          rows={byItem} rowKey={(r) => r.item}
          columns={[
            { key: 'item', header: 'Item', cell: (r) => <span className="font-bold">{r.item}</span> },
            { key: 'hsn', header: 'HSN', cell: (r) => r.hsn },
            { key: 'qty', header: 'Qty purchased', align: 'right', cell: (r) => money(r.qty) },
            { key: 'value', header: 'Purchase value', align: 'right', cell: (r) => money(r.taxable), value: (r) => r.taxable },
            { key: 'avgRate', header: 'Avg rate', align: 'right', cell: (r) => money(r.qty > 0 ? r.taxable / r.qty : 0), value: (r) => (r.qty > 0 ? r.taxable / r.qty : 0) },
            { key: 'vendor', header: 'Last vendor', cell: (r) => r.vendor },
          ]}
        />
      )}

      {view === 'hsn' && (
        <RegisterTable
          {...exp} title="By HSN (input-tax summary)" count={`${byHsn.length} HSN`} search={search} searchText={(r) => r.hsn} loading={query.isLoading}
          rows={byHsn} rowKey={(r) => r.hsn}
          columns={[
            { key: 'hsn', header: 'HSN', cell: (r) => <span className="font-extrabold text-accent">{r.hsn}</span> },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'gst', header: 'GST%', align: 'center', cell: (r) => <span className="tabular-nums">{r.gst}</span> },
            { key: 'tax', header: 'Input GST', align: 'right', cell: (r) => money(r.tax) },
            { key: 'total', header: 'Total', align: 'right', cell: (r) => money(r.total) },
          ]}
        />
      )}
    </div>
  );
}

function groupPurchase(lines: PurchaseLine[], keyOf: (l: PurchaseLine) => string) {
  const m = new Map<string, { bills: number; qty: number; taxable: number; inputGst: number; total: number; hsn: string; vendor: string; _bills: Set<string> }>();
  for (const l of lines) {
    const k = keyOf(l);
    let g = m.get(k);
    if (!g) { g = { bills: 0, qty: 0, taxable: 0, inputGst: 0, total: 0, hsn: l.hsn, vendor: l.vendor, _bills: new Set() }; m.set(k, g); }
    g._bills.add(l.grn); g.qty += l.qty; g.taxable += l.taxable; g.inputGst += l.inputGst; g.total += l.total; g.vendor = l.vendor;
  }
  return Array.from(m.entries())
    .map(([k, g]) => [k, { ...g, bills: g._bills.size }] as const)
    .sort((a, b) => b[1].taxable - a[1].taxable);
}

function groupHsn(lines: PurchaseLine[]) {
  const m = new Map<string, { qty: number; taxable: number; tax: number; total: number; rates: Set<number> }>();
  for (const l of lines) {
    let g = m.get(l.hsn);
    if (!g) { g = { qty: 0, taxable: 0, tax: 0, total: 0, rates: new Set() }; m.set(l.hsn, g); }
    g.qty += l.qty; g.taxable += l.taxable; g.tax += l.inputGst; g.total += l.total; g.rates.add(l.gst);
  }
  return Array.from(m.entries())
    .map(([hsn, g]) => ({ hsn, qty: g.qty, taxable: g.taxable, gst: Array.from(g.rates).sort((a, b) => a - b).join(' / '), tax: g.tax, total: g.total }))
    .sort((a, b) => b.taxable - a.taxable);
}
